import React, { useState, useEffect, useCallback } from 'react';
import '../IpPatientDetails.css';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TablePagination, TextField, Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { CommonPatientDetails } from '../../../../../Components/Common Components/CommonComponents';
import { getCachevalue, localGetItem } from '../../../../../Utility/Services/CacheProviderService';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../../../Components/Loader';
import { Colors } from '../../../../../Styles/Colors';

export default function PharmacyStock() {
  const [patientData, setPatientData] = useState({});
  const [withOutAppointment, setWithOutAppointment] = useState({});
  const [stockList, setStockList] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [refillOpen, setRefillOpen] = useState(false);
  const [refillItems, setRefillItems] = useState([]);
  const [refillQtyMap, setRefillQtyMap] = useState({});
  const [transmitLoading, setTransmitLoading] = useState(false);
  const [refillLogOpen, setRefillLogOpen] = useState(false);
  const [refillLogList, setRefillLogList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const ip_admission_id = patientData?.id || withOutAppointment?.ip_admission_id;

  const fetchPatient = useCallback(() => {
    const raw = getCachevalue('IppatientData');
    if (!raw) return;
    try {
      setPatientData(JSON.parse(raw));
    } catch (e) {}
    const appRaw = getCachevalue('withoutAppointment');
    if (appRaw) {
      try {
        const app = JSON.parse(appRaw);
        if (app?.patient_id === JSON.parse(raw)?.patient_id) setWithOutAppointment(app);
      } catch (e) {}
    }
  }, []);

  const fetchStockList = useCallback(() => {
    if (!ip_admission_id) return;
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_PATIENT_PHARMACY_STOCK}?ip_admission_id=${ip_admission_id}`)
      .then((res) => {
        if (res.data?.status === 'success') {
          setStockList(Array.isArray(res.data?.data) ? res.data.data : []);
        } else setStockList([]);
        setIsLoader(false);
      })
      .catch(() => {
        setStockList([]);
        setIsLoader(false);
      });
  }, [ip_admission_id]);

  const fetchRefillLog = useCallback(() => {
    if (!ip_admission_id) return;
    RestAPIService.getAll(`${Serviceurls.IP_REFILL_REQUEST_LOG}?ip_admission_id=${ip_admission_id}`)
      .then((res) => {
        if (res.data?.status === 'success') {
          setRefillLogList(Array.isArray(res.data?.data) ? res.data.data : []);
        } else setRefillLogList([]);
      })
      .catch(() => setRefillLogList([]));
  }, [ip_admission_id]);

  useEffect(() => { fetchPatient(); }, [fetchPatient]);
  useEffect(() => {
    if (ip_admission_id) {
      fetchStockList();
    }
  }, [ip_admission_id, fetchStockList]);

  const showError = (msg) => { setIsErrorMsg(true); setIsErrorMsgText(msg); };
  const showSuccess = (msg) => { setSuccessMsg(true); setSuccessMsgText(msg); };
  const msgClose = () => { setIsErrorMsg(false); setSuccessMsg(false); };

  const toggleSelect = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const toggleSelectAll = (checked) => {
    if (checked) setSelectedIds(stockList.map((r) => r.id));
    else setSelectedIds([]);
  };

  const openRefillPopup = () => {
    const items = stockList.filter((r) => selectedIds.includes(r.id));
    setRefillItems(items);
    const map = {};
    items.forEach((r) => { map[r.id] = ''; });
    setRefillQtyMap(map);
    setRefillOpen(true);
  };

  const setRefillQty = (id, value) => {
    setRefillQtyMap((prev) => ({ ...prev, [id]: value }));
  };

  const removeFromRefillList = (id) => {
    setRefillItems((prev) => prev.filter((r) => r.id !== id));
    setRefillQtyMap((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const handleRefillClear = () => {
    setRefillItems([]);
    setRefillQtyMap({});
    setSelectedIds([]);
    setRefillOpen(false);
  };

  const handleTransmit = () => {
    const withQty = refillItems.filter((r) => {
      const v = String(refillQtyMap[r.id] ?? '').trim();
      return v !== '' && !isNaN(parseInt(v, 10)) && parseInt(v, 10) > 0;
    });
    if (withQty.length === 0) {
      showError('Enter refill quantity for at least one drug.');
      return;
    }
    const payload = {
      ip_admission_id,
      drugs: withQty.map((r) => ({
        prescription_id: r.prescription_id,
        pharmacy_id: r.pharmacy_id,
        id: r.id,
        quantity: parseInt(String(refillQtyMap[r.id]).trim(), 10),
        uom: r.uom || 'g',
      })),
    };
    setTransmitLoading(true);
    RestAPIService.create(payload, Serviceurls.IP_PATIENT_PHARMACY_STOCK)
      .then((res) => {
        setTransmitLoading(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Refill request sent to pharmacy.');
          setRefillOpen(false);
          setRefillItems([]);
          setRefillQtyMap({});
          setSelectedIds([]);
          fetchStockList();
          fetchRefillLog();
        } else showError(res.data?.message || 'Transmit failed.');
      })
      .catch((err) => {
        setTransmitLoading(false);
        showError(err?.response?.data?.message || err?.message || 'Transmit failed.');
      });
  };

  const paginatedRows = stockList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ backgroundColor: Colors.configHomeBg, minHeight: '74dvh', p: '0.625vw' }}>
      <Box sx={{ mb: 2 }}>
        <CommonPatientDetails data={patientData} showDetailed={true} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          onClick={openRefillPopup}
          disabled={selectedIds.length === 0}
          sx={{ textTransform: 'uppercase', bgcolor: Colors.Primary || '#00897b' }}
        >
          Refill Request ({selectedIds.length})
        </Button>
        <Button
          variant="outlined"
          startIcon={<ScheduleIcon />}
          onClick={() => { fetchRefillLog(); setRefillLogOpen(true); }}
          sx={{ textTransform: 'capitalize' }}
        >
          Refill Log
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedIds.length > 0 && selectedIds.length < stockList.length}
                  checked={stockList.length > 0 && selectedIds.length === stockList.length}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Drug Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Strength</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Dosage Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Purchase Qty</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Available Qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => {
              const availQty = row.available_quantity ?? '-';
              return (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onChange={(e) => toggleSelect(row.id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{row.drug_name || row.generic_name || '-'}</TableCell>
                  <TableCell>{row.drug_strength ?? '-'}</TableCell>
                  <TableCell>{row.dosage_type ?? '-'}</TableCell>
                  <TableCell>{row.purchase_quantity ?? '-'}</TableCell>
                  <TableCell>{availQty}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={stockList.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 30, 50]}
          labelRowsPerPage="Rows per page:"
        />
      </Paper>

      {stockList.length === 0 && !isLoader && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No pharmacy stock records.</Typography>
      )}

      {/* Refill popup */}
      <Dialog open={refillOpen} onClose={() => setRefillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Refill
          <IconButton size="small" onClick={() => setRefillOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'warning.light', color: 'warning.dark', p: 1.5, borderRadius: 1, mb: 2 }}>
            <WarningAmberIcon fontSize="small" />
            <Typography variant="body2">
              This will send only the chosen drugs for refill requests to the pharmacy.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>Drug Details</Typography>
            <Typography variant="subtitle2" fontWeight={600}>Refill Quantity</Typography>
          </Box>
          {refillItems.map((row) => (
            <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <IconButton size="small" color="error" onClick={() => removeFromRefillList(row.id)} title="Remove from list">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{row.drug_name || row.generic_name || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.drug_strength ?? '-'} | {row.dosage_type ?? '-'}
                  </Typography>
                </Box>
              </Box>
              <TextField
                size="small"
                type="number"
                placeholder="Qty"
                value={refillQtyMap[row.id] ?? ''}
                onChange={(e) => setRefillQty(row.id, e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ width: 120 }}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleRefillClear} sx={{ textTransform: 'uppercase' }}>Clear</Button>
          <Button variant="contained" onClick={handleTransmit} disabled={transmitLoading} sx={{ textTransform: 'uppercase', bgcolor: Colors.Primary || '#00897b' }}>
            {transmitLoading ? 'Sending...' : 'Transmit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refill Log dialog */}
      <Dialog open={refillLogOpen} onClose={() => setRefillLogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Refill Log
          <IconButton size="small" onClick={() => setRefillLogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 600 }}>Requested Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Drug Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Strength</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Dosage Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested Qty</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Billed Qty</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {refillLogList.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.requested_date ? new Date(row.requested_date).toLocaleString() : '-'}</TableCell>
                  <TableCell>{row.drug_name || row.generic_name || '-'}</TableCell>
                  <TableCell>{row.drug_strength ?? '-'}</TableCell>
                  <TableCell>{row.dosage_type ?? '-'}</TableCell>
                  <TableCell>{row.requested_quantity ?? '-'}</TableCell>
                  <TableCell>{row.billed_quantity ?? '-'}</TableCell>
                  <TableCell>{row.status || '-'}</TableCell>
                  <TableCell>{row.requested_by || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {refillLogList.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No refill log entries.</Typography>
          )}
        </DialogContent>
      </Dialog>

      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
